import React, { useState, useEffect } from 'react';
import { heroData, socialLinks } from '../data/mockData';
import { Zap, Heart, Instagram, Linkedin, Mail, MapPin, Cloud, Youtube } from 'lucide-react';

const FloatingCallout = ({ text, emoji, style }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300 + (style.delay || 0));
    return () => clearTimeout(timer);
  }, [style.delay]);

  return (
    <>
      {/* Desktop callout */}
      <div
        className={`absolute z-30 transition-all duration-700 ease-out hidden md:block ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        }`}
        style={{
          top: style.top,
          left: style.left,
          right: style.right,
        }}
        aria-hidden="true"
      >
        <div 
          className="relative animate-float"
          style={{ animationDelay: `${(style.delay || 0) * 0.002}s` }}
        >
          <div className="bg-white/95 backdrop-blur-md px-2 py-1 rounded-full shadow-sm border border-stone-200 flex items-center gap-1 whitespace-nowrap text-[9px]">
            <span className="text-stone-600 font-medium">{text}</span>
            <span className="text-[10px]" role="img" aria-label={text}>{emoji}</span>
          </div>
        </div>
      </div>
      
      {/* Mobile callout */}
      <div
        className={`absolute z-30 transition-all duration-700 ease-out md:hidden ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        }`}
        style={{
          top: style.mobileTop || style.top,
          left: style.mobileLeft || style.left,
          right: style.mobileRight || style.right,
        }}
        aria-hidden="true"
      >
        <div 
          className="relative animate-float"
          style={{ animationDelay: `${(style.delay || 0) * 0.002}s` }}
        >
          <div className="bg-white/95 backdrop-blur-md px-1.5 py-0.5 rounded-full shadow-sm border border-stone-200 flex items-center gap-1 whitespace-nowrap text-[6px]">
            <span className="text-stone-600 font-medium">{text}</span>
            <span className="text-[7px]" role="img" aria-label={text}>{emoji}</span>
          </div>
        </div>
      </div>
    </>
  );
};

const PersonCard = ({ title, image, callouts, isDigital, calloutStyles }) => {
  return (
    <div className="relative flex flex-col items-center">
      {/* Modern title */}
      <div className="mb-1.5 md:mb-2">
        <span className={`text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase ${
          isDigital 
            ? 'text-stone-500' 
            : 'text-stone-800'
        }`}>
          {title}
        </span>
      </div>
      
      {/* Image container */}
      <div className="relative">
        {/* Floating callouts */}
        {callouts.map((callout, index) => (
          <FloatingCallout
            key={index}
            text={callout.text}
            emoji={callout.emoji}
            style={calloutStyles[index]}
          />
        ))}
        
        {/* Subtle glow for digital */}
        {isDigital && (
          <div className="absolute inset-0 bg-gradient-to-t from-stone-400/10 to-transparent rounded-2xl blur-xl" aria-hidden="true" />
        )}
        
        {/* Main image */}
        <div className={`relative overflow-hidden rounded-xl md:rounded-2xl ${
          isDigital 
            ? 'shadow-lg shadow-stone-300/30' 
            : 'shadow-lg shadow-stone-300/40'
        }`}>
          <img
            src={image}
            alt={isDigital ? "NAE-I digital avatar - Naomi's robot counterpart" : "Naomi Thomas - IRL portrait"}
            className={`w-28 h-40 md:w-44 md:h-60 lg:w-52 lg:h-68 object-cover transition-transform duration-500 hover:scale-105 ${
              !isDigital ? 'object-[50%_20%]' : 'object-[50%_22%]'
            }`}
          />
          
          {/* Digital overlay */}
          {isDigital && (
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/5 via-transparent to-transparent" aria-hidden="true" />
          )}
        </div>
        
        {/* Decorative icons below */}
        <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1" aria-hidden="true">
          {isDigital ? (
            <>
              <Zap className="w-2.5 h-2.5 text-stone-400" />
              <Cloud className="w-2.5 h-2.5 text-stone-300" />
            </>
          ) : (
            <>
              <Heart className="w-2.5 h-2.5 text-stone-400" fill="currentColor" />
              <Cloud className="w-2.5 h-2.5 text-stone-300" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Hero = () => {
  const { brandName, location, tagline, naomi, naeI } = heroData;

  // Callout positions
  const naomiCalloutStylesUpdated = [
    { top: '55%', left: '-75px', mobileLeft: '-52px', delay: 0 },
    { top: '-999%', right: '40px', mobileRight: '20px', delay: 200 },
    { top: '80%', left: '-55px', mobileLeft: '-40px', delay: 400 },
    { top: '55%', right: '-50px', mobileRight: '-35px', delay: 600 },
  ];
  
  const naeICalloutStyles = [
    { top: '60%', right: '-35px', mobileRight: '-25px', delay: 100 },
    { top: '72%', left: '-60px', mobileLeft: '-42px', delay: 300 },
    { top: '-999%', left: '30px', mobileLeft: '15px', delay: 500 },
    { top: '85%', right: '-45px', mobileRight: '-32px', delay: 700 },
  ];

  return (
    <section className="relative overflow-hidden bg-white" aria-labelledby="hero-heading">
      {/* Skip to main content link for keyboard users */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-stone-900 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg"
      >
        Skip to main content
      </a>
      
      {/* Cloud texture background - decorative */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute -top-20 -left-20 w-[400px] h-[300px] bg-gradient-to-br from-white via-stone-100 to-stone-200/80 rounded-full blur-[60px] animate-cloud-drift" />
        <div className="absolute top-0 right-0 w-[350px] h-[250px] bg-gradient-to-bl from-white via-pink-50 to-pink-100/60 rounded-full blur-[50px] animate-cloud-drift-slow" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/4 left-1/4 w-[300px] h-[200px] bg-gradient-to-r from-blue-50/70 via-white to-cyan-50/50 rounded-full blur-[40px] animate-cloud-drift" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/3 right-1/4 w-[280px] h-[180px] bg-gradient-to-l from-purple-50/60 via-white to-pink-50/40 rounded-full blur-[45px] animate-cloud-drift-slow" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-0 left-1/3 w-[320px] h-[220px] bg-gradient-to-t from-stone-100/80 via-white to-amber-50/40 rounded-full blur-[50px] animate-cloud-drift" style={{ animationDelay: '3s' }} />
        <div className="absolute bottom-10 right-10 w-[250px] h-[180px] bg-gradient-to-tl from-teal-50/50 via-white to-cyan-50/30 rounded-full blur-[40px] animate-cloud-drift-slow" style={{ animationDelay: '1.5s' }} />
        
        {/* Wispy cloud streaks */}
        <div className="absolute top-[10%] left-[5%] w-[300px] h-[40px] bg-gradient-to-r from-transparent via-white/60 to-transparent rounded-full blur-[20px] animate-cloud-drift" style={{ animationDelay: '0.5s' }} />
        <div className="absolute top-[20%] right-[10%] w-[250px] h-[30px] bg-gradient-to-r from-transparent via-stone-100/50 to-transparent rounded-full blur-[15px] animate-cloud-drift-slow" style={{ animationDelay: '1.5s' }} />
        <div className="absolute top-[35%] left-[15%] w-[200px] h-[25px] bg-gradient-to-r from-transparent via-pink-50/40 to-transparent rounded-full blur-[12px] animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute top-[50%] right-[20%] w-[280px] h-[35px] bg-gradient-to-r from-transparent via-white/50 to-transparent rounded-full blur-[18px] animate-cloud-drift" style={{ animationDelay: '1s' }} />
        <div className="absolute top-[65%] left-[8%] w-[220px] h-[28px] bg-gradient-to-r from-transparent via-cyan-50/30 to-transparent rounded-full blur-[14px] animate-cloud-drift-slow" style={{ animationDelay: '2.5s' }} />
        <div className="absolute top-[80%] right-[5%] w-[260px] h-[32px] bg-gradient-to-r from-transparent via-purple-50/30 to-transparent rounded-full blur-[16px] animate-float" style={{ animationDelay: '0.8s' }} />
        
        {/* Additional cloud wisps */}
        <div className="absolute top-[15%] left-[10%] w-[200px] h-[100px] bg-white/90 rounded-full blur-[30px] animate-float" />
        <div className="absolute top-[40%] right-[15%] w-[180px] h-[90px] bg-stone-50/80 rounded-full blur-[25px] animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-[20%] left-[20%] w-[220px] h-[110px] bg-white/70 rounded-full blur-[35px] animate-float" style={{ animationDelay: '2s' }} />
        
        {/* More cloud layers */}
        <div className="absolute top-[5%] right-[30%] w-[260px] h-[140px] bg-gradient-to-b from-white/80 to-stone-100/40 rounded-full blur-[45px] animate-cloud-drift" style={{ animationDelay: '2.5s' }} />
        <div className="absolute bottom-[30%] right-[5%] w-[200px] h-[120px] bg-gradient-to-tl from-pink-50/50 to-white/60 rounded-full blur-[35px] animate-float" style={{ animationDelay: '1.5s' }} />
        
        {/* Binary scatter - decorative */}
        <div className="font-mono text-[10px] text-stone-300/40 select-none overflow-hidden">
          <span className="absolute top-[8%] left-[5%]">01</span>
          <span className="absolute top-[12%] right-[8%]">10</span>
          <span className="absolute top-[25%] left-[15%]">11</span>
          <span className="absolute top-[30%] right-[20%]">00</span>
          <span className="absolute top-[45%] left-[8%]">01</span>
          <span className="absolute top-[50%] right-[12%]">10</span>
          <span className="absolute top-[65%] left-[25%]">11</span>
          <span className="absolute top-[70%] right-[5%]">01</span>
          <span className="absolute bottom-[15%] left-[12%]">00</span>
          <span className="absolute bottom-[10%] right-[18%]">10</span>
        </div>
      </div>
      
      {/* Main content */}
      <div className="relative z-10 container mx-auto px-4 pt-6 md:pt-8 pb-3 md:pb-4" id="main-content">
        {/* Brand name + location + socials */}
        <header className="text-center mb-4 md:mb-5">
          <h1 id="hero-heading" className="text-2xl md:text-4xl lg:text-5xl font-bold text-stone-900 tracking-tight">
            {brandName}
          </h1>
          
          {/* Location */}
          <div className="flex justify-center items-center gap-1 mt-1 text-stone-500">
            <MapPin className="w-2.5 h-2.5" aria-hidden="true" />
            <span className="text-[9px] md:text-[10px] tracking-wide lowercase">{location}</span>
          </div>
          
          {/* Social icons with proper labels */}
          <nav className="flex justify-center items-center gap-2 mt-2" aria-label="Social media links">
            <a 
              href={socialLinks.instagram} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-1.5 rounded-full bg-pink-100 hover:bg-pink-200 transition-colors focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
              aria-label="Follow on Instagram"
            >
              <Instagram className="w-3 h-3 text-pink-500" aria-hidden="true" />
            </a>
            <a 
              href={socialLinks.linkedin} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-1.5 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              aria-label="Connect on LinkedIn"
            >
              <Linkedin className="w-3 h-3 text-blue-500" aria-hidden="true" />
            </a>
            <a 
              href={socialLinks.email}
              className="p-1.5 rounded-full bg-orange-100 hover:bg-orange-200 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              aria-label="Send email"
            >
              <Mail className="w-3 h-3 text-orange-500" aria-hidden="true" />
            </a>
            <a 
              href={socialLinks.youtube} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-1.5 rounded-full bg-red-100 hover:bg-red-200 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              aria-label="Watch on YouTube"
            >
              <Youtube className="w-3 h-3 text-red-500" aria-hidden="true" />
            </a>
          </nav>
          
          {/* Intro text */}
          <div className="max-w-lg mx-auto mt-4 text-center">
            <p className="text-xs md:text-base text-stone-700 font-semibold leading-relaxed">
              Hey! My name is Naomi, also known as <span className="bg-pink-200/60 px-1.5 py-0.5 rounded">techbaenae</span>.
            </p>
            <p className="text-xs md:text-base text-stone-700 font-semibold leading-relaxed mt-1">
              I'm a <span className="bg-amber-200/60 px-1.5 py-0.5 rounded">product</span> and <span className="bg-green-200/60 px-1.5 py-0.5 rounded">marketing</span> strategist who thinks about
            </p>
            <p className="text-xs md:text-base text-stone-700 font-semibold leading-relaxed">
              <span className="bg-cyan-200/60 px-1 py-0.5 rounded">innovation</span> enhancing <span className="bg-purple-200/60 px-1 py-0.5 rounded">IRL + URL</span> experiences.
            </p>
          </div>
        </header>
        
        {/* Side by side layout */}
        <div className="flex flex-row justify-center items-start gap-8 md:gap-16 lg:gap-20 mb-3 md:mb-4 px-2" role="group" aria-label="IRL and URL personas">
          {/* Naomi - IRL */}
          <PersonCard
            title={naomi.title}
            image={naomi.image}
            callouts={naomi.callouts}
            isDigital={false}
            calloutStyles={naomiCalloutStylesUpdated}
          />
          
          {/* Bolt divider */}
          <div className="flex flex-col items-center justify-center h-40 md:h-60 lg:h-68" aria-hidden="true">
            <div className="w-px h-full bg-gradient-to-b from-transparent via-stone-300 to-transparent relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-1 bg-white rounded-full shadow-sm">
                <Zap className="w-2 h-2 md:w-2.5 md:h-2.5 text-stone-400" />
              </div>
            </div>
          </div>
          
          {/* NAE-I - URL */}
          <PersonCard
            title={naeI.title}
            image={naeI.image}
            callouts={naeI.callouts}
            isDigital={true}
            calloutStyles={naeICalloutStyles}
          />
        </div>
        
        {/* Tagline */}
        <p className="text-center text-[10px] md:text-xs text-stone-600 font-medium pb-4">
          {tagline}
        </p>
        
        {/* Description section */}
        <div className="px-2 pb-0">
          <div className="space-y-3 md:space-y-4 max-w-[340px] md:max-w-[450px] lg:max-w-[520px] mx-auto">
            {/* Phygital strategy block */}
            <div className="bg-stone-100 rounded-xl md:rounded-2xl py-3 md:py-5 px-2 md:px-3">
              <p className="text-[10px] md:text-sm text-stone-700 leading-relaxed text-center">
                <span className="text-purple-500 mr-1" role="img" aria-label="lightning">⚡</span>
                <span className="font-semibold text-purple-600">phygital strategy</span> is the language I use to
                <br />
                <span className="inline-block ml-6 md:ml-8">connect the physical &amp; digital worlds <span role="img" aria-label="chart and thought bubble">📈💭</span></span>
              </p>
            </div>
            
            {/* NAE-I block */}
            <div className="bg-stone-900 rounded-xl md:rounded-2xl py-3 md:py-5 px-2 md:px-3">
              <p className="text-[10px] md:text-sm text-stone-100 leading-relaxed text-center">
                <span className="text-teal-400 mr-1" role="img" aria-label="sparkle">✧</span>
                <span className="font-semibold text-teal-400">NAE-I</span> is a robot counterpart I created as an extension
                <br />
                of my brand, using visuals + systems thinking to
                <br />
                explore how technology shows up in everyday life <span role="img" aria-label="robot arm">🦾</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
